/**
 * Minimal Syntax Highlighter for SyntaxLens Mobile
 * Optimized for dark background with subtle highlighting
 */

interface MinimalColors {
  keyword: string      // Language constructs
  method: string       // Function/method calls
  punctuation: string  // Optional: dim brackets/operators
}

// Minimal color palette - matches your teal/purple design system
const COLORS: MinimalColors = {
  keyword: 'rgb(147 197 253 / 0.75)',      // Subtle blue (blue-300 with 75% opacity)
  method: 'rgb(103 232 249 / 0.7)',        // Subtle cyan (cyan-300 with 70% opacity)
  punctuation: 'rgb(203 213 225 / 0.6)'   // Very dim (slate-300 with 60% opacity)
}

// Comprehensive keyword set - essential language constructs across major languages
const KEYWORDS = new Set([
  // JavaScript/TypeScript
  'const', 'let', 'var', 'function', 'return', 'if', 'else', 'for', 'while', 'do',
  'switch', 'case', 'break', 'continue', 'default', 'throw', 'try', 'catch', 'finally',
  'async', 'await', 'yield', 'import', 'export', 'from', 'as', 'default',
  'class', 'extends', 'implements', 'interface', 'type', 'enum', 'namespace',
  'new', 'this', 'super', 'static', 'get', 'set', 'delete',
  'typeof', 'instanceof', 'in', 'of', 'with', 'debugger',
  'null', 'undefined', 'true', 'false', 'NaN', 'Infinity',
  // Common JavaScript built-ins
  'console', 'require', 'module', 'exports', 'Promise', 'Array', 'Object', 'String', 'Number',

  // Python
  'def', 'class', 'if', 'elif', 'else', 'for', 'while', 'in', 'return', 'yield',
  'import', 'from', 'as', 'with', 'lambda', 'pass', 'break', 'continue',
  'try', 'except', 'finally', 'raise', 'assert', 'del',
  'async', 'await', 'global', 'nonlocal', 'is', 'not', 'and', 'or',
  'None', 'True', 'False',
  // Common Python built-ins
  'print', 'len', 'range', 'enumerate', 'zip', 'map', 'filter', 'sorted',
  'int', 'str', 'float', 'bool', 'list', 'dict', 'set', 'tuple',

  // Rust
  'fn', 'let', 'mut', 'const', 'static', 'type', 'trait', 'impl', 'where',
  'struct', 'enum', 'union', 'match', 'if', 'else', 'loop', 'for', 'while', 'break', 'continue',
  'return', 'yield', 'move', 'ref', 'box', 'dyn',
  'mod', 'use', 'pub', 'crate', 'self', 'Self', 'super',
  'async', 'await', 'unsafe', 'extern', 'as',
  'true', 'false',
  // Common Rust macros (treated as keywords for highlighting)
  'println', 'print', 'eprintln', 'eprint', 'format',
  'panic', 'assert', 'assert_eq', 'assert_ne', 'debug_assert',
  'vec', 'todo', 'unimplemented', 'unreachable',
  'dbg', 'matches', 'cfg', 'include', 'include_str',

  // C/C++
  'int', 'char', 'float', 'double', 'void', 'bool', 'long', 'short',
  'signed', 'unsigned', 'auto', 'const', 'static', 'extern', 'register', 'volatile',
  'struct', 'union', 'enum', 'typedef',
  'if', 'else', 'switch', 'case', 'default', 'for', 'while', 'do', 'break', 'continue', 'goto',
  'return', 'sizeof', 'inline',
  'class', 'namespace', 'public', 'private', 'protected', 'virtual', 'template', 'typename',
  'new', 'delete', 'this', 'try', 'catch', 'throw',
  'true', 'false', 'nullptr', 'NULL',
  // Common C/C++ functions
  'printf', 'scanf', 'fprintf', 'sprintf', 'malloc', 'free', 'calloc', 'realloc',
  'strlen', 'strcpy', 'strcmp', 'memcpy', 'memset',
  'cout', 'cin', 'endl', 'std', 'vector', 'string', 'map',

  // Go
  'func', 'var', 'const', 'type', 'struct', 'interface', 'map', 'chan',
  'if', 'else', 'switch', 'case', 'default', 'fallthrough',
  'for', 'range', 'break', 'continue', 'goto',
  'return', 'defer', 'go', 'select',
  'import', 'package',
  'true', 'false', 'nil',
  // Common Go packages
  'fmt', 'error', 'make', 'len', 'cap', 'append', 'copy', 'delete',

  // Java
  'public', 'private', 'protected', 'static', 'final', 'abstract', 'synchronized',
  'volatile', 'transient', 'native', 'strictfp',
  'class', 'interface', 'extends', 'implements', 'package', 'import',
  'new', 'this', 'super', 'instanceof',
  'if', 'else', 'switch', 'case', 'default', 'for', 'while', 'do', 'break', 'continue',
  'return', 'try', 'catch', 'finally', 'throw', 'throws',
  'true', 'false', 'null',
  // Common Java built-ins
  'System', 'String', 'Integer', 'Double', 'Boolean', 'List', 'ArrayList', 'HashMap',

  // Ruby
  'def', 'class', 'module', 'if', 'elsif', 'else', 'unless', 'case', 'when',
  'for', 'while', 'until', 'loop', 'break', 'next', 'redo', 'retry',
  'return', 'yield', 'begin', 'rescue', 'ensure', 'raise',
  'do', 'end', 'then', 'and', 'or', 'not', 'in',
  'true', 'false', 'nil', 'self', 'super',

  // Swift
  'func', 'var', 'let', 'class', 'struct', 'enum', 'protocol', 'extension',
  'if', 'else', 'switch', 'case', 'default', 'guard', 'where',
  'for', 'in', 'while', 'repeat', 'break', 'continue', 'fallthrough',
  'return', 'throw', 'try', 'catch', 'defer',
  'import', 'typealias', 'associatedtype',
  'true', 'false', 'nil', 'self', 'Self', 'super',

  // Kotlin
  'fun', 'val', 'var', 'class', 'interface', 'object', 'data', 'sealed',
  'if', 'else', 'when', 'for', 'while', 'do', 'break', 'continue', 'return',
  'try', 'catch', 'finally', 'throw',
  'import', 'package', 'as', 'in', 'is',
  'true', 'false', 'null', 'this', 'super',
])

/**
 * Escape HTML special characters
 */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

/**
 * Wrap text with colored span
 */
function wrap(text: string, color: string): string {
  return `<span style="color: ${color}">${text}</span>`
}

/**
 * Minimal highlighting - only keywords and methods
 * Fast, lightweight, perfect for short code snippets
 */
export function highlightCode(code: string, _language?: string): string {
  if (!code || code.trim() === '') return escapeHtml(code)

  // Split code into tokens to avoid regex conflicts
  const tokens: Array<{ text: string; type: 'keyword' | 'method' | 'plain' }> = []

  // Tokenize the code
  const tokenRegex = /\b([a-zA-Z_][a-zA-Z0-9_]*)\b/g
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = tokenRegex.exec(code)) !== null) {
    // Add plain text before this token
    if (match.index > lastIndex) {
      tokens.push({
        text: code.substring(lastIndex, match.index),
        type: 'plain'
      })
    }

    const word = match[1]

    // Check if it's a keyword
    if (KEYWORDS.has(word)) {
      tokens.push({ text: word, type: 'keyword' })
    }
    // Check if it's a method call (followed by parenthesis)
    else if (code[match.index + word.length] === '(' ||
             (code[match.index + word.length] === ' ' &&
              code[match.index + word.length + 1] === '(')) {
      tokens.push({ text: word, type: 'method' })
    }
    // Otherwise it's plain text
    else {
      tokens.push({ text: word, type: 'plain' })
    }

    lastIndex = match.index + word.length
  }

  // Add remaining text
  if (lastIndex < code.length) {
    tokens.push({
      text: code.substring(lastIndex),
      type: 'plain'
    })
  }

  // Build the highlighted result
  return tokens.map(token => {
    const escaped = escapeHtml(token.text)
    if (token.type === 'keyword') {
      return wrap(token.text, COLORS.keyword)
    } else if (token.type === 'method') {
      return wrap(token.text, COLORS.method)
    } else {
      return escaped
    }
  }).join('')
}

/**
 * Simple highlighter class
 */
export class SyntaxHighlighter {
  static highlight(code: string, language?: string): string {
    return highlightCode(code, language)
  }
}

export default SyntaxHighlighter
